// ✅ src/pages/VehicleV2/LubricationRequestListing.jsx
// UPDATED FULL FILE
//
// Uses backend mount:
//   app.use("/api/LRListing", lubricationRequestListing);
//
// Expected endpoints:
//   GET /api/LRListing/init?branchId=&branchType=&loginUserId=
//   GET /api/LRListing/list?branchId=&month=&year=&search=&page=&pageSize=&loginUserId=
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + initLoading + listLoading
// - safer init / list flow
// - avoids repeated API calls
// - filter-change page reset without duplicate list firing
// - refresh button added
// - keeps pagination / print / details flow

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPlus,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/LRListing";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const normalizeBranchOptions = (arr) => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? "Select", "Select"),
    value: String(x.value ?? x.id ?? x.key ?? "Select"),
  }));

  return list.length ? list : [{ label: "Select", value: "Select" }];
};

const LubricationRequestListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  const itemsPerPage = 5;

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || initLoading || listLoading;

  /* ===========================
     ui states
  =========================== */
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  /* ===========================
     dropdown states
  =========================== */
  const [branches, setBranches] = useState([
    { label: "Select", value: "Select" },
  ]);
  const [years, setYears] = useState(["Select"]);
  const [months, setMonths] = useState(["Select"]);

  /* ===========================
     filters
  =========================== */
  const [filters, setFilters] = useState({
    branch: String(sessionBranchId),
    year: "Select",
    month: "Select",
  });

  /* ===========================
     data
  =========================== */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  /* ===========================
     refs
  =========================== */
  const initializedRef = useRef(false);
  const skipNextListRef = useRef(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(Number(total || 0) / itemsPerPage)),
    [total]
  );

  /* ===========================
     classes
  =========================== */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ===========================
     render helpers
  =========================== */
  const getBranchLabel = useCallback(
    (row) => row.branchName || row.BranchName || row.branch || row.fld_branchname || "-",
    []
  );

  const getMonth = useCallback(
    (row) => row.month || row.Month || row.fld_month || "-",
    []
  );

  const getYear = useCallback(
    (row) => row.year || row.Year || row.fld_year || "-",
    []
  );

  /* ===========================
     init load
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Failed to load initial data");
        return;
      }

      const nextBranches = normalizeBranchOptions(data?.branches);
      const nextYears = Array.isArray(data?.years) && data.years.length
        ? data.years
        : ["Select"];
      const nextMonths = Array.isArray(data?.months) && data.months.length
        ? data.months
        : ["Select"];

      const defaultBranchId = String(
        data?.defaultBranchId ?? sessionBranchId ?? "Select"
      );

      setBranches(nextBranches);
      setYears(nextYears);
      setMonths(nextMonths);

      setFilters((prev) => ({
        ...prev,
        branch: defaultBranchId,
      }));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load dropdowns"
      );
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [sessionBranchId, branchType, loginUserId, commonHeaders]);

  /* ===========================
     list load
  =========================== */
  const loadList = useCallback(
    async (page = 1, override = {}) => {
      const branchIdToSend = String(override.branch ?? filters.branch ?? "Select");
      const monthToSend = override.month ?? filters.month;
      const yearToSend = override.year ?? filters.year;
      const searchToSend = override.search ?? search;

      if (isSelect(branchIdToSend)) {
        setRows([]);
        setTotal(0);
        return;
      }

      try {
        setListLoading(true);

        const params = {
          branchId: branchIdToSend,
          month: isSelect(monthToSend) ? "Select" : monthToSend,
          year: isSelect(yearToSend) ? "Select" : yearToSend,
          search: toStr(searchToSend),
          page,
          pageSize: itemsPerPage,
          loginUserId,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "List failed");
        }

        setRows(toList(data?.rows));
        setTotal(Number(data?.total || 0));
        setCurrentPage(Number(data?.page || page));
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setListLoading(false);
      }
    },
    [filters.branch, filters.month, filters.year, search, loginUserId, commonHeaders]
  );

  /* ===========================
     handlers
  =========================== */
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAddNew = useCallback(() => {
    navigate("/vehicle/LubricationRequest");
  }, [navigate]);

  const handleViewDetails = useCallback(
    (row) => {
      const requestId = row.requestId ?? row.fld_lubricationrequestmasid ?? row.id;
      if (!requestId) {
        toast.info("RequestId not found in row");
        return;
      }

      navigate("/vehicle/LubricationRequest", {
        state: { requestId, row },
      });
    },
    [navigate]
  );

  const handlePrint = useCallback((row) => {
    const requestId = row.requestId ?? row.fld_lubricationrequestmasid ?? row.id;
    if (!requestId) {
      toast.info("RequestId not found in row");
      return;
    }

    const url = `/vehicle/LubricationRequestPrint?requestId=${encodeURIComponent(
      requestId
    )}`;
    window.open(url, "_blank", "width=900,height=900,scrollbars=yes");
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    skipNextListRef.current = true;
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    skipNextListRef.current = true;
    setCurrentPage(1);
    setSearch(e.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    skipNextListRef.current = true;
    setSearch("");
    setCurrentPage(1);
    setFilters({
      branch: String(sessionBranchId),
      year: "Select",
      month: "Select",
    });
    toast.info("Filters cleared!");
  }, [sessionBranchId]);

  const handleSearchClick = useCallback(async () => {
    await loadList(1);
  }, [loadList]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (initLoading) return;
    if (!filters.branch || filters.branch === "Select") return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      loadList(1, { branch: filters.branch });
    }
  }, [initLoading, filters.branch, loadList]);

  useEffect(() => {
    if (initLoading) return;
    if (!initializedRef.current) return;
    if (!filters.branch || filters.branch === "Select") return;

    if (skipNextListRef.current) {
      skipNextListRef.current = false;
      return;
    }

    loadList(currentPage);
  }, [
    currentPage,
    filters.branch,
    filters.month,
    filters.year,
    search,
    initLoading,
    loadList,
  ]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LUBRICATION REQUEST</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Lubrication Request Listing
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {sessionBranchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {total}</span>
                <span className={pillClass}>Page: {currentPage}</span>
                <span className={pillClass}>Mode: LIST</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800${showFilters ? " bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50${showFilters ? " bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter />
                FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={branchType !== "HO" || isBusy}
                  >
                    {branches.map((b, idx) => (
                      <option key={`${b.value}-${idx}`} value={String(b.value)}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select
                    name="year"
                    value={filters.year}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {years.map((y, idx) => (
                      <option key={`${y}-${idx}`} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select
                    name="month"
                    value={filters.month}
                    onChange={handleFilterChange}
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {months.map((m, idx) => (
                      <option key={`${m}-${idx}`} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="md:col-span-3">
                  <label className={labelClass}>Search</label>
                  <input
                    value={search}
                    onChange={handleSearchInputChange}
                    placeholder="Search by branch / month / year..."
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  type="button"
                  disabled={isBusy}
                  className={`font-medium transition-colors duration-200 ${
                    isDark
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-800"
                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  Clear All Filters
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleSearchClick}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    Search Records
                  </button>

                  <button
                    onClick={handleAddNew}
                    type="button"
                    disabled={isBusy}
                    className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {pageLoading || initLoading || listLoading ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Loading...
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Please wait while data is loading.
                </p>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Requests Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your search criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Branch</th>
                      <th className="px-4 py-4 text-center font-semibold">Month</th>
                      <th className="px-4 py-4 text-center font-semibold">Year</th>
                      <th className="px-4 py-4 text-center font-semibold">Details</th>
                      <th className="px-4 py-4 text-center font-semibold">Print</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((row, idx) => (
                      <tr
                        key={row.requestId ?? row.id ?? idx}
                        className={`${
                          isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getBranchLabel(row)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getMonth(row)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getYear(row)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handleViewDetails(row)}
                            type="button"
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded text-sm ${
                              isDark
                                ? "bg-blue-700 text-white hover:bg-blue-600"
                                : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                            }`}
                          >
                            <FaInfoCircle className="w-3 h-3" />
                            Details
                          </button>
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handlePrint(row)}
                            type="button"
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded text-sm ${
                              isDark
                                ? "bg-gray-700 text-white hover:bg-gray-600"
                                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                            }`}
                          >
                            <FaPrint className="w-3 h-3" />
                            Print
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && !isBusy && (
            <div className="mt-6 flex justify-center">
              <nav className="flex gap-1 flex-wrap">
                {Array.from({ length: totalPages }, (_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      type="button"
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === p
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

          {isBusy && (
            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading
                ? "Loading page..."
                : initLoading
                ? "Loading initial data..."
                : listLoading
                ? "Loading list..."
                : "Loading..."}
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubricationRequestListing;